/* React and Next */

import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

/* Material UI */

import {
  InputAdornment,
  IconButton,
  OutlinedInput,
  InputLabel,
  FormControl,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';

/* Icons */
import CreateOutlinedIcon from '@material-ui/icons/CreateOutlined';
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';

/* Validation */

import { useFormik } from 'formik';
import * as Yup from 'yup';

/* Local Imports */

import { AppDispatch } from '@/store/store';
import { checkEmail } from '@/features/auth/api/slice';
import Button from '@/features/markets/components/Button';
import Spacer from '@/components/ui-components/Spacer';

/* Style */
const useStyles = makeStyles((theme) => ({
  errors: {
    marginTop: theme.spacing(1),
  },
  inputfieldFullwidth: {
    width: '100%',
  },
}));

/* Interfaces */
interface Props {
  onComplete: (arg0: string) => void; // Show next form (Password or signup)
  onError: (arg0: string) => void; // Show next form (Password or signup)
  onCancel?: () => void; // Show next form (Password or signup)
  onSocial?: (arg0?: string) => void; // If the user used social medial login
  disabled?: boolean; // Don't show the button if I'm showing next form
}

const defaultProps = {
  disabled: false,
  onCancel: () => {},
  onSocial: () => {},
};

const EmailValidator: React.FC<Props> = ({
  onComplete,
  onCancel = () => {},
  onError,
  onSocial = () => {},
  disabled = false,
}: Props) => {
  /* Variables */
  const classes = useStyles();
  const { query } = useRouter();
  const { t } = useTranslation('users');
  const dispatch = useDispatch() as AppDispatch;
  const [loading, setLoading] = useState(false);

  /* Validation */
  const formik = useFormik({
    initialValues: {
      email: (query.email as string) || '',
    },

    validationSchema: Yup.object({
      email: Yup.string()
        .email(t('login.form_errors.email_not_valid'))
        .required(t('login.form_errors.required')),
    }),

    validateOnChange: false,

    validateOnBlur: false,

    onSubmit: (values, { validateForm }) => {
      const doCheckEmail = async () => {
        setLoading(true);
        dispatch(checkEmail({ email: values.email }))
          .then(unwrapResult)
          .then((originalPromiseResult) => {
            if (originalPromiseResult.exists) {
              // The email exists in the database
              if (
                originalPromiseResult.social_provider !== 'Google'
                && originalPromiseResult.social_provider !== 'Facebook'
              ) {
                // The login was no through SNS
                setLoading(false);
                onComplete(values.email);
              } else {
                setLoading(false);
                onSocial(values.email);
              }
            }
          })
          .catch(() => {
            // The email is not in the database
            setLoading(false);
            onError(values.email);
          });
      };
      validateForm(); // Validate form values
      doCheckEmail(); // Check if the Email exists in the database
    },
  });
  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        {/* EMAIL */}
        <FormControl className={classes.inputfieldFullwidth} variant="outlined">
          <InputLabel
            color="primary"
            htmlFor="email"
            margin="dense"
            error={!!formik.errors.email}
          >
            {t('login.email')}
          </InputLabel>
          <OutlinedInput
            id="email"
            name="email"
            label={t('login.email')}
            value={formik.values.email.toLowerCase().trim()}
            onChange={(e) => formik.setFieldValue(
              'email',
              e.currentTarget.value.toLowerCase().trim(),
            )}
            onBlur={formik.handleBlur}
            fullWidth
            autoFocus={!disabled}
            margin="dense"
            color="primary"
            disabled={disabled}
            error={!!formik.errors.email}
            endAdornment={
              disabled && (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="edit email address"
                    onClick={() => onCancel()}
                    onMouseDown={() => onCancel()}
                  >
                    <CreateOutlinedIcon />
                  </IconButton>
                </InputAdornment>
              )
            }
          />
        </FormControl>
        {/* Show email errors in the validation */}
        {formik.errors.email && (
          <Alert severity="error" className={classes.errors}>
            {formik.errors.email}
          </Alert>
        )}
        <Spacer spacing={1} />
        {/* SUBMIT BUTTON */}
        {/* Shows if the page only shows email input */}
        {!disabled && (
          <Button
            variant="contained"
            fullWidth
            color="primary"
            type="submit"
            endIcon={<ArrowRightAltIcon />}
            disabled={loading}
            text={
              !loading ? t('login.continue') : <CircularProgress size={17} />
            }
          />
        )}
      </form>
    </>
  );
};

EmailValidator.defaultProps = (defaultProps as unknown) as Partial<Props>;

export default EmailValidator;
